import { useEffect, useRef, useState, type ReactElement } from "react";
import type { BagSortOrder } from "odyssey-shared";
import { Button, KitInputProvider, Menu, SearchField, logical, type KitInput, type MenuEntry } from "../../ui/kit/index.ts";
import { foundCount } from "./describe.ts";
import { BAG_WINDOW } from "./layout.ts";
import { BAG_FILTERS, BAG_SEARCH_MAX, isLooking, type BagFilter, type BagLook } from "./looking.ts";
import type { BagSortState } from "./snapshot.ts";
import { BagSortMenu, sortingNote } from "./sort-menu.tsx";
import { say } from "../../words.ts";

/**
 * The Inventory window's tool row (P2.8-T45, ADR-125 decisions 2 and 4.2; the design's `InvTools` in the Inventory
 * card): above the grid, the kit's search field filling the row, then the Filter and Sort buttons, each with the 8 px
 * caret. Both are views on the page: nothing here reaches the bag or the server.
 *
 * - The field is "Search items". While text or a filter is on it shows the matching stacks over the stacks held,
 *   in the danger colour at 0; the cross clears the text. It answers Escape and Enter itself (the kit's search
 *   form): Escape clears its text, then hands the keyboard to the window (`onLeave`), whose next Escape closes it;
 *   Enter hands it over at once and keeps the text.
 * - Filter opens the kit's menu `menuGap` under the row, its right edge at the button's, headed "Show": All, Gear,
 *   Consumables, Materials and Quest, each with its count of stacks and the current one checked; choosing one sets
 *   the filter, and the button then reads the category in its selected look. The menu holds the keyboard as every kit
 *   menu does and closes on Escape, on Tab, on a choice and on a press outside it. With no input router (`input`
 *   absent) no menu can hold the keyboard, so Filter is drawn disabled.
 * - Sort opens the Sort menu (P2.8-T48, `sort-menu.tsx`) placed as Filter's is, with the last order sent checked;
 *   choosing one asks the bag to sort (`onSort`). While a sort waits for the server the button is busy, named by
 *   `sortingNote`. It is disabled with no input router, as Filter is, and where nothing can sort (`onSort` null).
 * - One menu is open at a time: opening one closes the other, and a press outside the open menu and its button
 *   closes it.
 */

/**
 * Which of the row's menus is open.
 */
type ToolMenu = "filter" | "sort" | null;

/**
 * The tool row.
 *
 * @param props The `look` the window holds and `onLook`, which sets it; the stacks the bag holds and how many of them
 *   match (`stacks`, `hits`); each filter entry's count (`counts`); the bag's `sort` state and `onSort`, which asks
 *   for a sort (null where nothing sorts); the page's input router (`input`), absent where no menu can open; and
 *   `onLeave`, which hands the keyboard from the field to the window.
 * @returns The row.
 */
export function BagTools({ look, onLook, stacks, hits, counts, sort, onSort, input, onLeave }: { readonly look: BagLook; readonly onLook: (look: BagLook) => void; readonly stacks: number; readonly hits: number; readonly counts: Readonly<Record<BagFilter, number>>; readonly sort: BagSortState; readonly onSort: ((by: BagSortOrder) => void) | null; readonly input: KitInput | undefined; readonly onLeave: () => void }): ReactElement {
  const [menu, setMenu] = useState<ToolMenu>(null);
  const filterBox = useRef<HTMLDivElement>(null);
  const sortBox = useRef<HTMLDivElement>(null);
  const looking = isLooking(look);

  useEffect(() => {
    if (menu === null) {
      return undefined;
    }
    const box = menu === "filter" ? filterBox : sortBox;
    /**
     * Closes the open menu on a press outside it and its button, which then does what it does there; a press on the
     * button is the button's own, which closes it.
     *
     * @param event The press.
     */
    const pressed = (event: PointerEvent): void => {
      if (!(event.target instanceof Node) || box.current?.contains(event.target) !== true) {
        setMenu(null);
      }
    };
    window.addEventListener("pointerdown", pressed, true);
    return () => window.removeEventListener("pointerdown", pressed, true);
  }, [menu]);

  /**
   * Opens a menu, or closes it when it is the one open.
   *
   * @param which The menu.
   */
  const toggle = (which: Exclude<ToolMenu, null>): void => {
    setMenu(menu === which ? null : which);
  };

  const entries: readonly MenuEntry<BagFilter>[] = BAG_FILTERS.map((filter) => ({ value: filter, label: say(`bag.tools.filters.${filter}`), note: String(counts[filter]), checked: look.filter === filter }));
  return (
    <div className="ot-bag__tools" data-bag="tools" style={{ columnGap: logical(BAG_WINDOW.toolGap), marginBottom: logical(BAG_WINDOW.rowGap) }}>
      <div className="ot-bag__search">
        <SearchField label={say("bag.tools.search")} placeholder={say("bag.tools.search")} value={look.query} onChange={(query) => onLook({ ...look, query })} maxLength={BAG_SEARCH_MAX} count={looking ? { text: foundCount(hits, stacks), none: hits === 0 } : null} clearLabel={say("bag.tools.clear")} onExit={onLeave} />
      </div>
      <div className="ot-bag__filter" data-bag="filter" ref={filterBox} style={{ maxWidth: logical(BAG_WINDOW.toolMax) }}>
        <Button selected={look.filter !== "all" || menu === "filter"} menuOpen={menu === "filter"} disabled={input === undefined} onClick={() => toggle("filter")}>
          {look.filter === "all" ? say("bag.tools.filter") : say(`bag.tools.filters.${look.filter}`)}
        </Button>
        {menu === "filter" && input !== undefined ? (
          <div className="ot-bag__tool-menu" data-bag="filter-menu" style={{ top: `calc(100% + ${logical(BAG_WINDOW.menuGap)})` }}>
            <KitInputProvider input={input}>
              <Menu
                label={say("bag.tools.filterMenu")}
                heading={say("bag.tools.filterHeading")}
                minWidth={BAG_WINDOW.filterMenuWidth}
                items={entries}
                onSelect={(filter) => {
                  setMenu(null);
                  onLook({ ...look, filter });
                }}
                onClose={() => setMenu(null)}
              />
            </KitInputProvider>
          </div>
        ) : null}
      </div>
      <div className="ot-bag__sort" data-bag="sort" ref={sortBox} style={{ maxWidth: logical(BAG_WINDOW.toolMax) }}>
        <Button selected={menu === "sort"} menuOpen={menu === "sort"} busy={sort.pending !== null} {...(sort.pending === null ? {} : { busyLabel: sortingNote(sort.pending) })} disabled={input === undefined || onSort === null} onClick={() => toggle("sort")}>
          {say("bag.tools.sort")}
        </Button>
        {menu === "sort" && input !== undefined && onSort !== null ? (
          <div className="ot-bag__tool-menu" style={{ top: `calc(100% + ${logical(BAG_WINDOW.menuGap)})` }}>
            <KitInputProvider input={input}>
              <BagSortMenu
                last={sort.last}
                onSelect={(by) => {
                  setMenu(null);
                  onSort(by);
                }}
                onClose={() => setMenu(null)}
              />
            </KitInputProvider>
          </div>
        ) : null}
      </div>
    </div>
  );
}
